import CodeIcon from "@material-ui/icons/Code";
import TimelineIcon from "@material-ui/icons/Timeline";
import StorageIcon from "@material-ui/icons/Storage";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import SecurityIcon from "@material-ui/icons/Security";
import InsertChartIcon from "@material-ui/icons/InsertChart";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AssessmentIcon from "@material-ui/icons/Assessment";
import GavelIcon from "@material-ui/icons/Gavel";
import EcoIcon from "@material-ui/icons/Eco";
import BuildIcon from "@material-ui/icons/Build";

// Streamlit lab instruction templates by finance persona (10 total).
// Each template tailors lab instructions for a distinct finance role.
// "content" is injected directly into the Instructions textarea.

const streamlitTemplates = [
  {
    heading: "Executive Stakeholder",
    icon: AttachMoneyIcon,
    description: "Strategic KPIs, macro trends, risk & opportunity summary.",
    content: `Persona: Executive Stakeholder (Finance)
Objective: Summarize top-line financial performance and emerging risks using attached datasets.
Sections:
  - KPI Board: revenue, EBITDA margin, YoY growth, VaR proxy.
  - Macro Overlay: benchmark index vs internal performance (auto-resample long horizons).
  - Risk & Opportunity Bullets: anomaly scan of segments (template if model absent).
  - Narrative Summary: concise paragraph (placeholder if LLM unavailable).
  - Export: one-click PDF/MD snapshot.
Constraints: Minimize jargon; hide raw tables behind toggle.
Tone: Concise, strategic.
Output: Implementation instructions for exec-facing Streamlit module.`
  },
  {
    heading: "Portfolio Manager",
    icon: TrendingUpIcon,
    description: "Allocation views, performance attribution, rebalancing sandbox.",
    content: `Persona: Portfolio Manager
Objective: Provide allocation insight & scenario-based rebalancing tools.
Features:
  - Current Allocation TreeMap + top holdings list.
  - Performance Attribution: contribution by sector/asset vs benchmark.
  - Scenario Sandbox: adjust weights → projected return/volatility (simplified calc).
  - Risk Overlay: correlation heatmap + max drawdown snapshot.
  - Rebalance Export: generate proposed trades CSV.
Constraints: Cache heavy metrics; fall back to simplified risk if data sparse.
Tone: Data-driven, actionable.
Output: Lab instructions for PM analytics workspace.`
  },
  {
    heading: "Quant Researcher",
    icon: CodeIcon,
    description: "Factor testing, signal diagnostics, backtest summaries.",
    content: `Persona: Quant Researcher
Objective: Explore factor signals & basic backtest evaluation.
Features:
  - Data Load & Cleaning summary.
  - Factor Explorer: select factor → distribution, IC (information coefficient) over time.
  - Signal Diagnostics: turnover, decay chart (approximate if data limited).
  - Backtest Panel: run simple long-short or ranking strategy; show cumulative return vs benchmark.
  - Experiment Log: parameter + result metrics table (exportable).
Constraints: Keep computations vectorized; sample if dataset large.
Tone: Technical, precise.
Output: Instructions for quant factor analysis Streamlit lab.`
  },
  {
    heading: "Risk Manager",
    icon: SecurityIcon,
    description: "Exposure breakdown, stress testing sliders, VaR approximation.",
    content: `Persona: Risk Manager
Objective: Provide multi-dimensional exposure & sensitivity interface.
Features:
  - Exposure Dashboard: sector, region, instrument type.
  - Stress Test: sliders for shock scenarios (e.g., rate +100bps, FX moves) → P&L impact approximation.
  - VaR Approximation: historical simulation (fallback parametric if insufficient history).
  - Alerts Panel: highlight positions breaching thresholds.
  - Export: scenario report (CSV/MD).
Constraints: Ensure deterministic fallback paths; handle missing data gracefully.
Tone: Vigilant, structured.
Output: Lab instructions for risk analytics module.`
  },
  {
    heading: "Compliance Officer",
    icon: GavelIcon,
    description: "Policy tracking, flagged transaction review, audit trail export.",
    content: `Persona: Compliance Officer
Objective: Surface potential policy breaches & provide review workflow scaffolding.
Features:
  - Policy Overview: summary of active compliance rules (static template if not provided).
  - Flagged Transactions Table: sortable/filterable with rule tags.
  - Detail Drilldown: expand row → contextual metadata & placeholder rationale.
  - Review Queue: mark status (Accepted, Investigate, Escalate) with timestamp.
  - Audit Export: produce JSON/CSV of actions taken.
Constraints: No real PII exposure; mask sensitive fields.
Tone: Controlled, precise.
Output: Instructions for compliance review Streamlit lab.`
  },
  {
    heading: "Retail Investor Education",
    icon: CloudUploadIcon,
    description: "Guided learning, glossary, simplified risk demonstrations.",
    content: `Persona: Retail Investor (Educational)
Objective: Teach fundamental portfolio & risk concepts interactively.
Features:
  - Learning Objectives Banner.
  - Step-by-Step Tabs: Diversification → Volatility → Correlation → Simple Allocation.
  - Glossary: hover highlights & popover definitions.
  - Interactive Sliders: adjust weights to see diversification effect.
  - Reflection Notes: user text saved & exportable.
Constraints: Limit complexity; avoid dense math—include intuitive tooltips.
Tone: Supportive, clear.
Output: Lab instructions for investor education module.`
  },
  {
    heading: "Algorithmic Trader",
    icon: TimelineIcon,
    description: "Signal feed, order simulation, latency & slippage approximation.",
    content: `Persona: Algorithmic Trader
Objective: Prototype strategy signal visualization & order execution simulation.
Features:
  - Live/Simulated Signal Stream (mock if not real-time).
  - Order Simulator: size, limit/market toggle, slippage estimate.
  - Strategy Performance: equity curve, drawdown chart, trade list.
  - Latency Metrics: placeholder distribution (if data absent, synthetic example).
  - Parameter Adjust: fast re-run with modified thresholds.
Constraints: Avoid implying real execution; mark simulation clearly.
Tone: Tactical, performance-focused.
Output: Instructions for algo trading mock lab.`
  },
  {
    heading: "ESG Analyst",
    icon: EcoIcon,
    description: "ESG scoring breakdown, controversy flags, weighted portfolio impact.",
    content: `Persona: ESG Analyst
Objective: Provide ESG score decomposition & portfolio impact evaluation.
Features:
  - Score Summary: E/S/G pillar averages vs benchmark.
  - Controversy Flags: list entities with issues (template if data absent).
  - Pillar Drilldown: interactive bar chart with weight-adjust sliders.
  - Portfolio Impact: simulate excluding lowest decile → new aggregate score & return delta.
  - Export: recommended exclusions list.
Constraints: Handle missing scores; indicate estimation where applied.
Tone: Analytical, responsible.
Output: Instructions for ESG evaluation Streamlit lab.`
  },
  {
    heading: "Financial Data Engineer",
    icon: BuildIcon,
    description: "Data pipeline health, schema diffs, lineage & quality metrics.",
    content: `Persona: Financial Data Engineer
Objective: Monitor ingestion & transformation quality.
Features:
  - Pipeline Status Tiles: last run time, success/failure, record counts.
  - Schema Diff Viewer: highlight added/removed columns vs prior snapshot.
  - Quality Metrics: missing %, duplicate count, outlier candidate tally.
  - Lineage Diagram: simplified static representation (placeholder if no graph data).
  - Alert Configuration: thresholds editable with persistence stub.
Constraints: Avoid heavy graph libs if absent; degrade gracefully.
Tone: Operational, precise.
Output: Instructions for data pipeline monitoring lab.`
  },
];

export default streamlitTemplates;
